import { Step, StepLayoutChildProps, useSteps } from '@consigli/facade';
import { FC, useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DocumentsStep, LocationStep, SiteNameStep, SummaryStep, UrlsStep } from './steps';
import {
  defaultFormValues,
  useWizardContext,
  WizardFormData,
  WizardProvider,
} from './wizard-context';
import { SiteDueDiligenceUploadInfo } from '../components/upload-info-panel';
import { TabType, WizardLayout } from '../components/wizard-layout';
import { useCreateSiteDueDiligencePackage } from '../hooks/use-create-site-due-diligence-package';
import { useUploadProgress } from '../hooks/use-upload-progress';

const WizardContent = () => {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [targetCounter, setTargetCounter] = useState(0);
  const [currentCounter, setCurrentCounter] = useState(0);

  // Form data managed via react-hook-form
  const methods = useForm<WizardFormData>({
    defaultValues: defaultFormValues,
    mode: 'onChange',
  });

  const { isUploading, progress } = useUploadProgress(currentCounter, targetCounter);

  const { onAnalyze: handleAnalyze } = useCreateSiteDueDiligencePackage({
    setCurrentCounter,
    setTargetCounter,
  });

  const {
    facilityType,
    templateMode,
    customTemplateFile,
    selectedCustomer,
    documentFiles,
    gisFiles,
    urls,
    allowLLMForDocuments,
  } = useWizardContext();

  const onAnalyze = useCallback(async () => {
    const formData = methods.getValues();
    const wizardData = {
      facilityType,
      templateMode,
      customTemplateFile,
      selectedCustomer,
      documentFiles,
      gisFiles,
      urls,
      allowLLMForDocuments,
    };

    await handleAnalyze(formData, wizardData, setIsProcessing);
  }, [
    methods,
    facilityType,
    templateMode,
    customTemplateFile,
    selectedCustomer,
    documentFiles,
    gisFiles,
    urls,
    allowLLMForDocuments,
    handleAnalyze,
  ]);

  // Define steps with useSteps from facade
  const { withStep, StepIndicator, StepOutlet, useStepContext } = useSteps(
    (step: (name: string, component: FC<StepLayoutChildProps>) => Step) => {
      const steps: Step[] = [
        /*  step(t('package-creation.step-facility-type'), () => (
          <FacilityTypeStep context={useStepContext} />
        )),
          step(t('package-creation.step-template'), () => <TemplateStep context={useStepContext} />), */
      ];

      // Only include customer step for DATA_CENTER facility type
      /* if (facilityType === SiteDueDiligenceFacilityType.DATA_CENTER) {
        steps.push(
          step(t('package-creation.step-customer'), () => (
            <CustomerStep context={useStepContext} />
          )),
        );
      } */

      steps.push(
        step(t('package-creation.step-site-name'), () => <SiteNameStep context={useStepContext} />),
        step(t('package-creation.step-location'), () => <LocationStep context={useStepContext} />),
        step(t('package-creation.step-documents'), () => (
          <DocumentsStep context={useStepContext} />
        )),
        step(t('package-creation.step-urls'), () => <UrlsStep context={useStepContext} />),
        step(t('package-creation.step-summary'), () => (
          <SummaryStep context={useStepContext} onAnalyze={onAnalyze} />
        )),
      );

      return steps as [Step, ...Step[]];
    },
  );

  const CreatePackageForm = withStep(() => (
    <>
      <StepIndicator />
      <FormProvider {...methods}>
        <StepOutlet />
      </FormProvider>
    </>
  ));

  const messageHeader = <SiteDueDiligenceUploadInfo />;

  return (
    <WizardLayout
      isProcessing={isProcessing}
      messageHeader={messageHeader}
      isUploading={isUploading}
      progress={progress}
      tabContent={{
        [TabType.CREATE_PACKAGE]: CreatePackageForm,
      }}
    />
  );
};

export const SiteDueDiligenceWizard = () => {
  return (
    <WizardProvider>
      <WizardContent />
    </WizardProvider>
  );
};
